import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useAuth } from '../../contexts/AuthContext';
import { useStripeContext } from '../../contexts/StripeContext';
import Icon from '../../components/AppIcon';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import stripePaymentService from '../../services/stripePaymentService';

// ─── Payment Form ────────────────────────────────────────────────────────────
const BidPaymentForm = ({ clientSecret, bidData, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);

  useEffect(() => {
    if (!elements) return;
    const el = elements?.getElement(PaymentElement);
    if (el) {
      el?.on('ready', () => setPaymentReady(true));
    }
  }, [elements]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }
    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location?.origin}/checkout-confirmation` },
        redirect: 'if_required',
      });

      if (stripeError) {
        setErrorMessage(stripeError?.message || 'Payment failed. Please try again.');
        onError?.(stripeError);
      } else if (paymentIntent) {
        if (paymentIntent?.status === 'requires_capture' || paymentIntent?.status === 'succeeded') {
          const { error: confirmError } = await stripePaymentService?.confirmEscrowPayment(paymentIntent?.id);
          if (confirmError) {
            setErrorMessage('Payment processed but confirmation failed. Please contact support.');
          } else {
            onSuccess?.(paymentIntent);
          }
        } else {
          setErrorMessage('Payment processing incomplete. Please try again.');
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
      onError?.(err);
    } finally {
      setIsProcessing(false);
    }
  };

  const bidAmount = parseFloat(bidData?.bidAmount || 0);
  const platformFee = stripePaymentService?.calculatePlatformFee(bidAmount);
  const total = bidAmount + platformFee;

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3" />
        <div className="h-12 bg-muted rounded" />
        <div className="h-4 bg-muted rounded w-1/2" />
        <div className="h-12 bg-muted rounded" />
        <p className="text-xs text-muted-foreground text-center pt-2">Loading payment system…</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Order Summary */}
      <div className="bg-muted/20 border border-border/60 rounded-xl p-4 space-y-3">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Order Summary</p>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Winning Bid</span>
          <span className="font-medium text-foreground">{stripePaymentService?.formatAmount(bidAmount)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Platform Fee (5%)</span>
          <span className="font-medium text-foreground">{stripePaymentService?.formatAmount(platformFee)}</span>
        </div>
        <div className="pt-2 border-t border-border flex justify-between items-center">
          <span className="font-semibold text-foreground">Total Due</span>
          <span className="text-xl font-bold text-primary">{stripePaymentService?.formatAmount(total)}</span>
        </div>
      </div>
      {/* Escrow Notice */}
      <div className="flex items-start gap-3 bg-emerald-950/30 border border-emerald-800/40 rounded-xl p-4">
        <div className="w-8 h-8 rounded-lg bg-emerald-900/50 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Icon name="ShieldCheck" size={16} className="text-emerald-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-emerald-300 mb-0.5">Escrow Protected</p>
          <p className="text-xs text-emerald-400/80 leading-relaxed">
            Your payment is held securely until both parties confirm the transaction. Funds are only released when you confirm receipt.
          </p>
        </div>
      </div>
      {/* Stripe Payment Element */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Payment Details</label>
        <div className="rounded-xl border border-border overflow-hidden p-3 bg-card">
          <PaymentElement options={{ layout: 'tabs', fields: { billingDetails: 'auto' } }} />
        </div>
      </div>
      {/* Error */}
      {errorMessage && (
        <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 rounded-xl p-4">
          <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
          <p className="text-sm text-destructive">{errorMessage}</p>
        </div>
      )}
      {/* Submit */}
      <Button
        type="submit"
        disabled={!paymentReady || isProcessing || !stripe || !elements}
        loading={isProcessing}
        iconName={isProcessing ? 'Loader2' : 'Lock'}
        iconPosition="left"
        fullWidth
        size="lg"
        className="liquid-gold"
      >
        {isProcessing ? 'Processing Payment…' : `Pay ${stripePaymentService?.formatAmount(total)}`}
      </Button>
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Icon name="Shield" size={13} />
        <span>Secured by Stripe · Your payment information is encrypted</span>
      </div>
    </form>
  );
};

// ─── Success Screen ───────────────────────────────────────────────────────────
const SuccessScreen = ({ bidData, paymentIntent, onGoToDashboard }) => {
  const navigate = useNavigate();
  const confirmationId = `CRC-${Date.now()?.toString(36)?.toUpperCase()}`;

  return (
    <div className="flex flex-col items-center text-center space-y-6 py-4">
      {/* Animated checkmark */}
      <div className="relative">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-warning flex items-center justify-center shadow-gold"
          style={{ animation: 'successPop 0.55s cubic-bezier(0.34,1.56,0.64,1) forwards' }}>
          <Icon name="Check" size={36} color="white" />
        </div>
        <div className="absolute inset-0 rounded-full border-4 border-primary/30 animate-ping opacity-40" />
      </div>
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-1">Payment Confirmed</p>
        <h2 className="text-2xl font-bold text-foreground mb-2">Bid Secured! 🎉</h2>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto leading-relaxed">
          Your payment is held in escrow. The seller has been notified and will confirm the transaction.
        </p>
      </div>
      {/* Details */}
      <div className="w-full bg-muted/20 border border-border rounded-xl p-4 space-y-3 text-left">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Confirmation ID</span>
          <span className="font-mono font-semibold text-foreground text-xs">{confirmationId}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Item</span>
          <span className="font-medium text-foreground text-right max-w-[180px] truncate">{bidData?.auctionTitle || '—'}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Amount Paid</span>
          <span className="font-bold text-primary">{stripePaymentService?.formatAmount(parseFloat(bidData?.bidAmount || 0) + stripePaymentService?.calculatePlatformFee(parseFloat(bidData?.bidAmount || 0)))}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Escrow Status</span>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-warning inline-block" />
            <span className="text-sm font-medium text-warning">Held in Escrow</span>
          </div>
        </div>
      </div>
      <div className="w-full flex flex-col sm:flex-row gap-3">
        <Button
          variant="default"
          fullWidth
          iconName="LayoutDashboard"
          iconPosition="left"
          onClick={onGoToDashboard}
          className="liquid-gold"
        >
          View My Bids
        </Button>
        <Button
          variant="outline"
          fullWidth
          iconName="Gavel"
          iconPosition="left"
          onClick={() => navigate('/auction-floor')}
        >
          Back to Auctions
        </Button>
      </div>
    </div>
  );
};

// ─── Main Checkout Page ───────────────────────────────────────────────────────
const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile } = useAuth();
  const { stripePromise, stripeOptions } = useStripeContext();

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [successPaymentIntent, setSuccessPaymentIntent] = useState(null);

  // Bid data passed via navigation state or query params
  const params = new URLSearchParams(location?.search);
  const bidData = location?.state?.bidData || {
    auctionId: params?.get('auctionId'),
    auctionTitle: params?.get('title') || 'Auction Item',
    bidAmount: parseFloat(params?.get('amount') || '0'),
    sellerId: params?.get('sellerId'),
    sellerName: params?.get('sellerName') || 'Seller',
    featuredImageUrl: params?.get('imageUrl') || null,
    currency: params?.get('currency') || 'USD',
    offerId: params?.get('offerId'),
    listingId: params?.get('listingId'),
  };

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!bidData?.bidAmount || bidData?.bidAmount <= 0) {
      setError('Invalid bid data. Please return to the auction and try again.');
      setLoading(false);
      return;
    }
    initPayment();
  }, [user]);

  const initPayment = async () => {
    setLoading(true);
    setError(null);

    const offerData = {
      offer_id: bidData?.offerId || null,
      listing_id: bidData?.listingId || bidData?.auctionId,
      listing_title: bidData?.auctionTitle,
      seller_id: bidData?.sellerId,
      offer_amount: bidData?.bidAmount,
      currency: bidData?.currency || 'USD',
    };

    const buyerInfo = {
      id: userProfile?.id,
      full_name: userProfile?.full_name,
      email: userProfile?.email,
      stripeCustomerId: userProfile?.stripe_customer_id || null,
    };

    const { data, error: paymentError } = await stripePaymentService?.createEscrowPayment(offerData, buyerInfo);

    if (paymentError) {
      setError(paymentError);
    } else {
      setClientSecret(data?.clientSecret);
    }
    setLoading(false);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    setSuccessPaymentIntent(paymentIntent);
    setPaymentSuccess(true);
  };

  const handlePaymentError = (err) => {
    console.error('Payment error:', err);
  };

  const bidAmount = parseFloat(bidData?.bidAmount || 0);
  const platformFee = stripePaymentService?.calculatePlatformFee(bidAmount);
  const total = bidAmount + platformFee;

  const elementsOptions = {
    ...stripeOptions,
    clientSecret,
    appearance: {
      ...stripeOptions?.appearance,
      theme: 'night',
      variables: {
        colorPrimary: '#d4af37',
        colorBackground: '#1a1a1a',
        colorText: '#e5e5e5',
        colorDanger: '#ef4444',
        fontFamily: 'DM Sans, system-ui, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
    },
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <div className="flex flex-1 pt-16">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />

        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'} p-6 lg:p-8`}>
          <div className="max-w-5xl mx-auto">

            {/* Page Header */}
            <div className="mb-8">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
              >
                <Icon name="ArrowLeft" size={16} />
                <span>Back</span>
              </button>
              <h1 className="text-2xl font-bold text-foreground">Complete Your Bid</h1>
              <p className="text-muted-foreground text-sm mt-1">
                Secure your winning bid with escrow-protected payment
              </p>
            </div>

            {/* Success State */}
            {paymentSuccess ? (
              <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl p-8 shadow-premium-lg">
                <style>{`
                  @keyframes successPop {
                    0%   { transform: scale(0.5); opacity: 0; }
                    70%  { transform: scale(1.12); }
                    100% { transform: scale(1); opacity: 1; }
                  }
                `}</style>
                <SuccessScreen
                  bidData={bidData}
                  paymentIntent={successPaymentIntent}
                  onGoToDashboard={() => navigate('/buyer-auction-dashboard')}
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

                {/* Left: Item Summary */}
                <div className="lg:col-span-2 space-y-4">
                  {/* Item Card */}
                  <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
                    <div className="relative w-full h-52 bg-muted">
                      {bidData?.featuredImageUrl ? (
                        <AppImage
                          src={bidData?.featuredImageUrl}
                          alt={bidData?.auctionTitle || 'Auction item being purchased'}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                          <Icon name="Package" size={40} className="text-muted-foreground" />
                          <p className="text-xs text-muted-foreground">No image available</p>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/90 text-primary-foreground backdrop-blur-sm">
                          <Icon name="Gavel" size={11} />
                          Winning Bid
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-semibold text-foreground text-base leading-snug mb-1">
                        {bidData?.auctionTitle || 'Auction Item'}
                      </h3>
                      <p className="text-xs text-muted-foreground mb-4">
                        Seller: <span className="text-foreground font-medium">{bidData?.sellerName || '—'}</span>
                      </p>

                      <div className="space-y-2.5">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Your Bid</span>
                          <span className="font-semibold text-foreground">{stripePaymentService?.formatAmount(bidAmount)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Platform Fee (5%)</span>
                          <span className="font-medium text-foreground">{stripePaymentService?.formatAmount(platformFee)}</span>
                        </div>
                        <div className="pt-2.5 border-t border-border flex justify-between items-center">
                          <span className="font-bold text-foreground">Total</span>
                          <span className="text-lg font-bold text-primary">{stripePaymentService?.formatAmount(total)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Trust Signals */}
                  <div className="bg-card border border-border rounded-2xl p-5 space-y-3">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Why Bid with Confidence</p>
                    {[
                      { icon: 'ShieldCheck', label: 'Escrow Protection', desc: 'Funds held until you confirm receipt' },
                      { icon: 'RefreshCw', label: 'Dispute Resolution', desc: 'Full support if issues arise' },
                      { icon: 'Lock', label: 'Encrypted Payments', desc: 'Secured by Stripe' },
                    ]?.map((item) => (
                      <div key={item?.label} className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name={item?.icon} size={14} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground">{item?.label}</p>
                          <p className="text-xs text-muted-foreground">{item?.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Payment Form */}
                <div className="lg:col-span-3">
                  <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon name="CreditCard" size={18} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-base font-semibold text-foreground">Payment Details</h2>
                        <p className="text-xs text-muted-foreground">Complete your bid securely</p>
                      </div>
                    </div>

                    {/* Loading */}
                    {loading && (
                      <div className="space-y-4 animate-pulse">
                        <div className="h-4 bg-muted rounded w-1/3" />
                        <div className="h-12 bg-muted rounded" />
                        <div className="h-4 bg-muted rounded w-1/2" />
                        <div className="h-12 bg-muted rounded" />
                        <div className="h-10 bg-muted rounded mt-4" />
                        <p className="text-xs text-muted-foreground text-center">Initializing secure payment…</p>
                      </div>
                    )}

                    {/* Error */}
                    {!loading && error && (
                      <div className="space-y-4">
                        <div className="flex items-start gap-3 bg-destructive/10 border border-destructive/20 rounded-xl p-4">
                          <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-semibold text-destructive mb-1">Payment Initialization Failed</p>
                            <p className="text-xs text-destructive/80">{error}</p>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <Button variant="outline" fullWidth onClick={initPayment} iconName="RefreshCw" iconPosition="left">
                            Retry
                          </Button>
                          <Button variant="ghost" fullWidth onClick={() => navigate(-1)} iconName="ArrowLeft" iconPosition="left">
                            Go Back
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Payment Form */}
                    {!loading && !error && clientSecret && (
                      <Elements stripe={stripePromise} options={elementsOptions}>
                        <BidPaymentForm
                          clientSecret={clientSecret}
                          bidData={bidData}
                          onSuccess={handlePaymentSuccess}
                          onError={handlePaymentError}
                        />
                      </Elements>
                    )}
                  </div>

                  {/* Buyer Info */}
                  {user && (
                    <div className="mt-4 bg-muted/20 border border-border/50 rounded-xl p-4 flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        {userProfile?.avatar_url ? (
                          <img src={userProfile?.avatar_url} alt="Buyer profile avatar" className="w-9 h-9 rounded-full object-cover" />
                        ) : (
                          <Icon name="User" size={16} className="text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs text-muted-foreground">Paying as</p>
                        <p className="text-sm font-semibold text-foreground truncate">{userProfile?.full_name || user?.email}</p>
                        <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Checkout;
